import api from './api'
import { serializeDynamicFilters } from '../components/common/table'

export interface Campaign {
  id: string
  smb_id: string
  name: string
  campaign_type: 'broadcast' | 'targeted'
  campaign_status: 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled' | 'partially_completed' | 'failed' | 'waiting_for_quota' | 'waiting_for_template_approval'
  template_id?: string
  channel_id?: string
  start_at?: string
  end_at?: string
  send_window_start?: string
  send_window_end?: string
  timezone: string
  created_at: string
  updated_at: string
  description?: string
  status_before_pause?: string
  latest_execution_log?: LatestExecutionLog | null
  user_facing_failure_reason?: string | null
}

export interface LatestExecutionLog {
  id: string
  status: string
  execution_started_at: string
  next_retry_at?: string | null
}

export interface CampaignCreateRequest {
  name: string
  campaign_type?: 'broadcast' | 'targeted'
  channel_id?: string
  description?: string
  template_id?: string
  variable_mappings?: VariableMapping[]
  audience_type?: 'upload' | 'master' // Audience type: upload (CSV) or master (master leadbook)
  timezone?: string
  start_at?: string
  end_at?: string
  send_window_start?: string
  send_window_end?: string
}

export interface CampaignUpdateRequest {
  name?: string
  campaign_type?: 'broadcast' | 'targeted'
  channel_id?: string
  description?: string
  timezone?: string
  start_at?: string
  end_at?: string
  send_window_start?: string
  send_window_end?: string
}

export interface CampaignFilters {
  status?: string
  campaign_type?: string
  limit?: number
  offset?: number
}

export interface CampaignListResponse {
  campaigns: Campaign[]
  total?: number
}

export interface VariableMapping {
  csv_column: string
  template_var: string
  user_label?: string
}

export interface TemplateAttachRequest {
  template_id: string
  variable_mappings: VariableMapping[]
}

export interface ColumnMapping {
  [key: string]: string // e.g., { "name": "name", "phone": "phone", "order_id": "order_id" }
}

export interface ScheduleRequest {
  start_at: string // ISO datetime string
  end_at?: string // ISO datetime string
  timezone: string
  send_window_start?: string // HH:mm:ss format
  send_window_end?: string // HH:mm:ss format
  audience_type?: 'upload' | 'master' // Audience type: upload (CSV) or master (master leadbook)
}

export interface ButtonStat {
  button_text: string
  count: number
}

export interface CampaignStats {
  total_sent: number
  total_delivered: number
  total_failed: number
  delivery_rate: number
  button_stats?: ButtonStat[]
}

export interface LeadLevelStats {
  total_leads: number
  leads_delivered: number
  leads_read: number
  leads_failed: number
  leads_sent: number
  delivery_rate: number
  read_rate: number
}

export interface LeadBook {
  id: string
  campaign_id: string
  book_type: 'campaign_audience' | 'master_lead_book'
  parameter_count: Record<string, any>
  uploaded_at: string
  preview?: any[]
  total_leads?: number
}

export interface ExecutionLog {
  id: string
  status: 'running' | 'completed' | 'failed' | 'partial'
  execution_started_at: string
  execution_completed_at?: string
  total_leads: number
  successful_leads: number
  failed_leads: number
  pending_leads?: number
  error_summary?: Record<string, any>
  failure_details?: any[]
  next_retry_at?: string
}

export interface ExecutionLogListResponse {
  execution_logs: ExecutionLog[]
  total: number
  limit: number
  offset: number
}

export interface Message {
  id: string
  platform_message_id?: string
  message_type?: string
  content?: string
  is_sent: boolean
  is_delivered: boolean
  is_failed: boolean
  is_read: boolean
  created_at?: string
  sent_at?: string
  delivered_at?: string
  read_at?: string
  lead_name?: string
  lead_phone?: string
  conversation_id?: string
}

export interface MessageListResponse {
  messages: Message[]
  total: number
  limit: number
  offset: number
}

const campaignApi = {
  // Campaign CRUD
  createCampaign: async (smbId: string, data: CampaignCreateRequest): Promise<Campaign> => {
    const response = await api.post(`/campaigns?smb_id=${smbId}`, data)
    return response.data
  },

  getCampaign: async (id: string): Promise<Campaign> => {
    const response = await api.get(`/campaigns/${id}`)
    return response.data
  },

  listCampaigns: async (smbId: string, filters?: CampaignFilters): Promise<CampaignListResponse> => {
    const params = new URLSearchParams({ smb_id: smbId })
    if (filters?.status) params.append('status', filters.status)
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.offset) params.append('offset', filters.offset.toString())
    
    const response = await api.get(`/campaigns?${params.toString()}`)
    return { campaigns: response.data, total: response.data.length }
  },

  updateCampaign: async (id: string, data: CampaignUpdateRequest): Promise<Campaign> => {
    const response = await api.patch(`/campaigns/${id}`, data)
    return response.data
  },

  cancelCampaign: async (id: string): Promise<Campaign> => {
    const response = await api.post(`/campaigns/${id}/cancel`)
    return response.data
  },

  // Template Management
  attachTemplate: async (id: string, templateId: string, variableMappings: VariableMapping[]): Promise<Campaign> => {
    const response = await api.patch(`/campaigns/${id}/template`, {
      template_id: templateId,
      variable_mappings: variableMappings,
    })
    return response.data
  },

  // Audience Management
  uploadAudience: async (id: string, file: File, columnMappings: ColumnMapping): Promise<LeadBook> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('column_mappings', JSON.stringify(columnMappings))
    
    const response = await api.post(`/campaigns/${id}/audience`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Scheduling
  scheduleCampaign: async (id: string, schedule: ScheduleRequest): Promise<Campaign> => {
    const response = await api.post(`/campaigns/${id}/schedule`, schedule)
    return response.data
  },

  // Statistics
  getCampaignStats: async (id: string): Promise<CampaignStats> => {
    const response = await api.get(`/campaigns/${id}/stats`)
    return response.data
  },

  getCampaignLeadStats: async (id: string): Promise<LeadLevelStats> => {
    const response = await api.get(`/campaigns/${id}/lead-stats`)
    return response.data
  },

  // Messages
  getCampaignMessagesStatsFilters: async (
    id: string,
    columnName: string,
    limit?: number,
    offset?: number,
    sortOrder?: string
  ): Promise<{ items: string[]; total_count: number; has_more: boolean; next_offset: number; has_empty_values?: boolean }> => {
    const params = new URLSearchParams()
    params.append('column_name', columnName)
    if (limit !== undefined) params.append('limit', limit.toString())
    if (offset !== undefined) params.append('offset', offset.toString())
    if (sortOrder) params.append('sort_order', sortOrder)
    
    const response = await api.get(`/campaigns/${id}/messages-stats/filters?${params.toString()}`)
    return response.data
  },

  getCampaignMessagesStats: async (
    id: string,
    filters?: {
      limit?: number
      offset?: number
      search?: string
      sortBy?: string
      sortOrder?: string
      dynamicFilters?: Record<string, string[]>
    }
  ): Promise<MessageListResponse> => {
    const params = new URLSearchParams()
    if (filters?.limit !== undefined) params.append('limit', filters.limit.toString())
    if (filters?.offset !== undefined) params.append('offset', filters.offset.toString())
    if (filters?.search) params.append('search', filters.search)
    if (filters?.sortBy) params.append('sort_by', filters.sortBy)
    if (filters?.sortOrder) params.append('sort_order', filters.sortOrder)
    const serializedFilters = serializeDynamicFilters(filters?.dynamicFilters)
    if (serializedFilters) {
      params.append('filters', serializedFilters)
    }
    
    const response = await api.get(`/campaigns/${id}/messages-stats?${params.toString()}`)
    return response.data
  },

  // Execution Logs
  getExecutionLogs: async (id: string, filters?: { limit?: number; offset?: number }): Promise<ExecutionLogListResponse> => {
    const params = new URLSearchParams()
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.offset) params.append('offset', filters.offset.toString())

    const response = await api.post(`/campaigns/${id}/execution-logs?${params.toString()}`)
    return response.data
  },

  // Delete Campaign
  deleteCampaign: async (id: string): Promise<void> => {
    await api.delete(`/campaigns/${id}`)
  },

  // Pause Campaign
  pauseCampaign: async (id: string): Promise<Campaign> => {
    const response = await api.post(`/campaigns/${id}/pause`)
    return response.data
  },

  // Resume Campaign
  resumeCampaign: async (id: string): Promise<Campaign> => {
    const response = await api.post(`/campaigns/${id}/resume`)
    return response.data
  },

  // Validate CSV columns for empty cells
  validateCsvColumns: async (smbId: string, file: File, requiredLeadColumns: string[], requiredTemplateColumns: string[]): Promise<{ missing_lead_data: boolean, missing_template_data: boolean, invalid_phone_format: boolean, has_duplicate_rows?: boolean, has_duplicate_phone?: boolean, invalid_csv_url?: string | null }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('required_lead_columns', JSON.stringify(requiredLeadColumns))
    formData.append('required_template_columns', JSON.stringify(requiredTemplateColumns))

    const response = await api.post(`/campaigns/${smbId}/validate-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  downloadStats: async (id: string): Promise<string> => {
    const response = await api.get(`/campaigns/${id}/detailed-stats`)
    const publicUrl = response.data?.public_url as string | undefined

    if (!publicUrl) {
      throw new Error('Stats download URL is missing from server response.')
    }

    return publicUrl
  }
  
}

export default campaignApi
